class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (window.resetCheckoutState) window.resetCheckoutState();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const quantityInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
    const lineVariantId = variantId || quantityInput?.dataset.quantityVariantId;
    const lineKey = quantityInput?.dataset.quantityLineKey;

    // Daily Magnesium Complex: dynamically sync Kaching bundle discount properties based on quantity
    if (String(lineVariantId) === '47595571150997') {
      this.enableLoading(line);

      let kachingProps = {};
      if (quantity === 2) {
        kachingProps = {
          '__kaching_bundles': JSON.stringify({
            deal: 'tGIs',
            pp: 9294227898517,
            main: true,
            bid: 'PVcF',
            ab: 'A'
          })
        };
      } else if (quantity >= 3) {
        kachingProps = {
          '__kaching_bundles': JSON.stringify({
            deal: 'tGIs',
            pp: 9294227898517,
            main: true,
            bid: 'EhE4',
            ab: 'A'
          })
        };
      }

      const sectionsToRender = this.getSectionsToRender();
      const currentPath = window.location.pathname;
      const searchStr = window.location.search || '';
      const fullSectionUrl = currentPath + (searchStr ? searchStr + '&' : '?') + 'sections=cart-drawer,cart-icon-bubble';

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey || line,
          quantity: 0
        })
      })
      .then(() => {
        if (quantity > 0) {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: 47595571150997,
              quantity: quantity,
              properties: kachingProps,
              sections: sectionsToRender.map((s) => s.section),
              sections_url: currentPath + searchStr
            })
          }).then((r) => r.json());
        } else {
          return fetch(fullSectionUrl).then((r) => r.json());
        }
      })
      .then((parsedState) => {
        const sections = parsedState.sections || parsedState;
        if (sections && sections['cart-drawer']) {
          const htmlString = sections['cart-drawer'];
          const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
          const newDrawer = parsed.querySelector('cart-drawer');
          const currentDrawer = document.querySelector('cart-drawer');
          if (newDrawer && currentDrawer) {
            var wasActive = currentDrawer.classList.contains('active');
            currentDrawer.replaceWith(newDrawer);
            if (wasActive) {
              newDrawer.classList.add('active', 'animate');
              document.body.classList.add('overflow-hidden');
            } else {
              newDrawer.classList.remove('active', 'animate');
              document.body.classList.remove('overflow-hidden');
            }
            if (window.resetCheckoutState) window.resetCheckoutState();
          }
          const iconBubble = document.querySelector('#cart-icon-bubble');
          if (iconBubble && sections['cart-icon-bubble']) {
            const parsedIcon = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const newBubble = parsedIcon.querySelector('#cart-icon-bubble') || parsedIcon.querySelector('.shopify-section');
            if (newBubble) iconBubble.innerHTML = newBubble.innerHTML;
          }
        }
      })
      .catch((err) => {
        console.error('[CartDrawerItems updateQuantity Error]', err);
        this.disableLoading(line);
      });

      return;
    }

    super.updateQuantity(line, quantity, event, name, variantId);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
